import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  Grid,
  Card,
  CardHeader,
  CardContent,
  CircularProgress,
} from '@mui/material';
import RestoreIcon from '@mui/icons-material/Restore';
import { FieldDefinition, Field } from '../../services/metadataService';
import { SecondaryButton } from '../common/ActionButtons';

interface RevertOverrideDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  overrideItem: FieldDefinition | Field | any | null;
  globalItem: FieldDefinition | Field | any | null;
  loading?: boolean;
  itemType: 'fieldDefinition' | 'field' | 'view';
  relatedFields?: Field[];
}

export const RevertOverrideDialog: React.FC<RevertOverrideDialogProps> = ({
  open,
  onClose,
  onConfirm,
  overrideItem,
  globalItem,
  loading = false,
  itemType,
  relatedFields = [],
}) => {
  const isFieldDefinition = itemType === 'fieldDefinition';
  const isView = itemType === 'view';
  const itemLabel = isFieldDefinition ? 'Field Definition' : isView ? 'View' : 'Field';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RestoreIcon color="warning" />
          Revert {itemLabel} to Global Version
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Data Loss Warning:</strong> This action will permanently remove your
            tenant-specific
            {isFieldDefinition
              ? ' field definition override and all its related fields'
              : isView
                ? ' view override and all its field-view relationships'
                : ' field override'}
            . The global version will become visible instead.
          </Typography>
        </Alert>

        <Grid container spacing={2}>
          <Grid size={{ xs: 6 }}>
            <Card variant="outlined">
              <CardHeader
                title={`Current ${itemLabel} (Will Be Removed)`}
                titleTypographyProps={{ variant: 'h6' }}
                sx={{ pb: 1 }}
              />
              <CardContent sx={{ pt: 0 }}>
                <Typography>
                  <strong>Label:</strong>{' '}
                  {isFieldDefinition
                    ? (overrideItem as FieldDefinition)?.label
                    : isView
                      ? (overrideItem as any)?.label
                      : (overrideItem as Field)?.label_override || 'Loading...'}
                </Typography>
                {isFieldDefinition ? (
                  <>
                    <Typography>
                      <strong>Data Type:</strong>{' '}
                      {(overrideItem as FieldDefinition)?.datatype || 'Loading...'}
                    </Typography>
                  </>
                ) : isView ? (
                  <>
                    <Typography>
                      <strong>View ID:</strong> {(overrideItem as any)?.view_id || 'Loading...'}
                    </Typography>
                    <Typography>
                      <strong>Model:</strong> {(overrideItem as any)?.model_id || 'Loading...'}
                    </Typography>
                    <Typography>
                      <strong>View Type:</strong> {(overrideItem as any)?.view_type || 'Loading...'}
                    </Typography>
                    <Typography>
                      <strong>Fields:</strong> {(overrideItem as any)?.field_count || 0} field
                      {(overrideItem as any)?.field_count !== 1 ? 's' : ''}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography>
                      <strong>Model:</strong> {(overrideItem as Field)?.model_id || 'Loading...'}
                    </Typography>
                    <Typography>
                      <strong>Storage:</strong>{' '}
                      {(overrideItem as Field)?.storage_type || 'Loading...'}
                    </Typography>
                    <Typography>
                      <strong>Required:</strong>{' '}
                      {(overrideItem as Field)?.is_required ? 'Yes' : 'No'}
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 6 }}>
            <Card variant="outlined">
              <CardHeader
                title="Global Version (Will Become Visible)"
                titleTypographyProps={{ variant: 'h6' }}
                sx={{ pb: 1 }}
              />
              <CardContent sx={{ pt: 0 }}>
                <Typography>
                  <strong>Label:</strong>{' '}
                  {isFieldDefinition
                    ? (globalItem as FieldDefinition)?.label
                    : isView
                      ? (globalItem as any)?.label
                      : (globalItem as Field)?.label_override || 'Loading...'}
                </Typography>
                {isFieldDefinition ? (
                  <>
                    <Typography>
                      <strong>Data Type:</strong>{' '}
                      {(globalItem as FieldDefinition)?.datatype || 'Loading...'}
                    </Typography>
                  </>
                ) : isView ? (
                  <>
                    <Typography>
                      <strong>View ID:</strong> {(globalItem as any)?.view_id || 'Loading...'}
                    </Typography>
                    <Typography>
                      <strong>Model:</strong> {(globalItem as any)?.model_id || 'Loading...'}
                    </Typography>
                    <Typography>
                      <strong>View Type:</strong> {(globalItem as any)?.view_type || 'Loading...'}
                    </Typography>
                    <Typography>
                      <strong>Fields:</strong> {(globalItem as any)?.field_count || 0} field
                      {(globalItem as any)?.field_count !== 1 ? 's' : ''}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography>
                      <strong>Model:</strong> {(globalItem as Field)?.model_id || 'Loading...'}
                    </Typography>
                    <Typography>
                      <strong>Storage:</strong>{' '}
                      {(globalItem as Field)?.storage_type || 'Loading...'}
                    </Typography>
                    <Typography>
                      <strong>Required:</strong> {(globalItem as Field)?.is_required ? 'Yes' : 'No'}
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Related Fields Section - only show for field definitions with related fields */}
        {isFieldDefinition && relatedFields.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: 'warning.main' }}>
              Related Fields That Will Be Affected ({relatedFields.length})
            </Typography>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                The following {relatedFields.length} field{relatedFields.length !== 1 ? 's' : ''}{' '}
                will also be affected by this revert:
              </Typography>
            </Alert>
            <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
              {relatedFields.map((field, index) => (
                <Box
                  key={field.field_id}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 1,
                    borderBottom: index < relatedFields.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider',
                  }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {field.label_override || field.field_definition_label || field.fielddef_id}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Model: {field.model_name || field.model_id} • Storage: {field.storage_type}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {field.tenant_id}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <SecondaryButton onClick={onClose} disabled={loading}>
          Cancel
        </SecondaryButton>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="warning"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <RestoreIcon />}
        >
          {loading ? 'Reverting...' : `Revert to Global ${itemLabel}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RevertOverrideDialog;
